import { z } from "zod";

/**
 * The adapter kinds ELASO knows about, the value persisted in
 * `AdapterInstance.kind`.
 *
 * The enum is shared. Each kind's **config shape deliberately is not.** Those
 * live with the adapter that owns them (`apps/server/src/adapters/<kind>/config.ts`)
 * and are composed into one discriminated union in
 * `apps/server/src/adapters/config.ts`. Nothing outside the server parses an
 * adapter's config, and keeping the shapes out of this package is what stops a
 * client component from importing LTI internals and hard-coding one
 * adapter's vocabulary into the UI, the coupling the `Adapter` interface exists
 * to prevent.
 *
 * The enum itself has to be here, because it is a persisted column value, and
 * `CreateOrganizationInputSchema` in schemas/platform.ts, an RPC input the
 * client validates against, takes its `adapterKind` straight from this enum, so
 * a renamed kind fails to compile at the boundary. This package cannot import
 * from `apps/server`.
 */
export const AdapterKindSchema = z.enum(["strapi", "lti"]);
export type AdapterKind = z.infer<typeof AdapterKindSchema>;

/**
 * A tenant-key column of an adapter registration, `externalIssuer` or
 * `externalClientId`. Normalised so the column holds a non-empty string or
 * `null` and nothing in between. The uniqueness guarantee behind the pair is a
 * partial index filtered on `$gt: ""`, which cannot see an empty string, so a
 * row written with `""` would be exempt from it. An admin clearing the field
 * sends `""`, and that has to land as `null`.
 */
export const TenantKeyFieldSchema = z
	.string()
	.trim()
	.nullish()
	.transform((value) => value || null);
